import { AnchorHTMLAttributes } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";

type CtaButtonProps = {
    readonly text: string;
    readonly ctaButtonUrl: string;
    readonly bgColor: string;
    readonly textColor: string;
    readonly analyticsEventLabel?: string;
    readonly radius?: string;
    readonly hoverColor?: string | undefined;
    readonly fontSize?: string;
    readonly weight?: string;
    readonly paddingNorthSouth?: string;
    readonly paddingEastWest?: string;
};

export const CtaButton = ({
    text,
    ctaButtonUrl,
    bgColor,
    textColor,
    analyticsEventLabel,
    radius = "4",
    hoverColor = undefined,
    fontSize = "16",
    weight = "500",
    paddingNorthSouth = "10",
    paddingEastWest = "16",
    ...props
}: CtaButtonProps & AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SOFTWARE,
        edition: Editions.FREE,
        redirectUrl: ctaButtonUrl
    });
    const handleClick = (e: React.MouseEvent) => {
        // if analyticsEventLabel fire second event
        if (analyticsEventLabel) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                attributes: {
                    name: `${analyticsEventLabel}`,
                    section: "cta-section"
                }
            }).fire();
        }
        if (openCrossFlow) {
            openCrossFlow(e);
        }
    };

    const headerBtnLgPrimaryDarkBlue = css({
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontSize: `${fontSize}px`,
        fontWeight: weight,
        lineHeight: "20px",
        textAlign: "center",
        color: textColor,
        background: bgColor,
        border: "none",
        borderRadius: `${radius}px`,
        outline: "none",
        transition: "300ms",
        minWidth: "124px",
        padding: `${paddingNorthSouth}px ${paddingEastWest}px`
    });
    const hover = css({
        ["&:hover"]: {
            backgroundColor: hoverColor
        }
    });

    return (
        <a
            onClick={handleClick}
            href={ctaButtonUrl}
            css={[headerBtnLgPrimaryDarkBlue, hoverColor && hover]}
            {...props}
        >
            {text}
        </a>
    );
};
